import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Reusable Date Filter Component
 * Provides consistent date filtering across all pages
 * 
 * @param {Object} props
 * @param {Function} props.onFilterChange - Callback when filter changes, receives { filterType, exactDate, startDate, endDate }
 * @param {string} props.label - Label for the filter (default: "Date Filter")
 */
const DateFilter = ({ onFilterChange, label = "Date Filter" }) => {
  const [dateFilterType, setDateFilterType] = useState("all");
  const [exactDate, setExactDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Calculate date range based on filter type
  const getDateRange = (filterType) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    let start = "";
    let end = "";

    // Helper function to format date as YYYY-MM-DD
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    switch (filterType) {
      case "this_month":
        start = formatDate(new Date(year, month, 1));
        end = formatDate(new Date(year, month + 1, 0));
        break;
      case "previous_month":
        // Previous month: first day to last day of previous month
        start = formatDate(new Date(year, month - 1, 1));
        end = formatDate(new Date(year, month, 0));
        break;
      case "this_quarter":
        const quarter = Math.floor(month / 3);
        start = formatDate(new Date(year, quarter * 3, 1));
        end = formatDate(new Date(year, quarter * 3 + 3, 0));
        break;
      case "this_year":
        // Financial year: April to March
        const fiscalYearStart = month >= 3 ? year : year - 1;
        start = `${fiscalYearStart}-04-01`;
        end = `${fiscalYearStart + 1}-03-31`;
        break;
      default:
        break;
    }
    return { start, end };
  };

  // Notify parent component when filter changes
  useEffect(() => {
    if (onFilterChange) {
      let filterData = {
        filterType: dateFilterType,
        exactDate: "",
        startDate: "",
        endDate: ""
      };

      if (dateFilterType === "exact") {
        filterData.exactDate = exactDate;
      } else if (dateFilterType === "month") {
        if (selectedMonth) {
          const [year, month] = selectedMonth.split("-");
          const y = parseInt(year);
          const m = parseInt(month);
          const lastDay = new Date(y, m, 0).getDate();
          filterData.startDate = `${year}-${month}-01`;
          filterData.endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
        }
      } else if (dateFilterType === "custom") {
        filterData.startDate = startDate;
        filterData.endDate = endDate;
      } else if (["this_month", "previous_month", "this_quarter", "this_year"].includes(dateFilterType)) {
        const { start, end } = getDateRange(dateFilterType);
        filterData.startDate = start;
        filterData.endDate = end;
      }

      onFilterChange(filterData);
    }
  }, [dateFilterType, exactDate, startDate, endDate, selectedMonth]);

  const handleFilterTypeChange = (value) => {
    setDateFilterType(value);
    if (value !== "exact") setExactDate("");
    if (value !== "month") setSelectedMonth("");
    if (value !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{label}</Label>
        <Select value={dateFilterType} onValueChange={handleFilterTypeChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Dates</SelectItem>
            <SelectItem value="exact">Exact Date</SelectItem>
            <SelectItem value="month">Specific Month</SelectItem>
            <SelectItem value="custom">Custom Date Range</SelectItem>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="previous_month">Previous Month</SelectItem>
            <SelectItem value="this_quarter">This Quarter</SelectItem>
            <SelectItem value="this_year">This Year (Financial)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Conditional date inputs */}
      {dateFilterType === "exact" && (
        <div className="space-y-2">
          <Label>Select Date</Label>
          <Input
            type="date"
            value={exactDate}
            onChange={(e) => setExactDate(e.target.value)}
          />
        </div>
      )}

      {dateFilterType === "month" && (
        <div className="space-y-2">
          <Label>Select Month</Label>
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            data-testid="date-filter-month"
          />
        </div>
      )}

      {dateFilterType === "custom" && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="cursor-pointer relative z-10"
              placeholder="dd-mm-yyyy"
              style={{ WebkitAppearance: 'none' }}
            />
          </div>
          <div className="space-y-2">
            <Label>End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="cursor-pointer relative z-10"
              placeholder="dd-mm-yyyy"
              style={{ WebkitAppearance: 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilter;
